import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import type { RefObject, MutableRefObject } from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { compile, serialize, stringify } from 'stylis';
import type { Editor as TiptapEditor } from '@tiptap/core';

import { useTheme } from '@pega/cosmos-react-core';
import { getHtmlStyles } from '@pega/cosmos-react-core/lib/components/HTML/HTML';
import {
  createGlobalBodyStyles,
  createGlobalRootStyles,
  globalSpacingStyles
} from '@pega/cosmos-react-core/lib/styles/GlobalStyle';

import type { CustomComponent, EditorProps } from '../Editor.types';
import { IframeTiptapEditor } from '../IframeTiptapEditor';
import { getIframeContentStyles } from '../iframeContentStyles';

interface UseIframeSetupParams {
  iframeRef: RefObject<HTMLIFrameElement>;
  theme: ReturnType<typeof useTheme>;
  styleSheetTarget?: HTMLElement | ShadowRoot;
  customElements: CustomComponent[];
  placeholder?: string;
  defaultValue?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: (editor: TiptapEditor) => void;
  onKeyDown?: (event?: KeyboardEvent) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onInit?: (editor: TiptapEditor) => void;
  spellcheck?: boolean;
  initOptions?: EditorProps['initOptions'];
  onImageAdded?: (image: File, id: string, altText?: string) => void;
  imagesEnabled: boolean;
  linksEnabled: boolean;
  colorsEnabled: boolean;
  imageInsertionMode: 'file' | 'url' | 'all';
  pastedImages: MutableRefObject<File[]>;
  editorId: string;
  required?: boolean;
  setTiptapEditor: (editor: TiptapEditor) => void;
  setFocused: (focused: boolean) => void;
  autoResize?: boolean;
  onTabOut?: () => void;
  onFocusTableMenu?: () => void;
  onFocusPreviousCellMenu?: () => void;
  secure?: boolean;
  preserveTableStyles?: boolean;
}

export interface IframeEditorHandle {
  focus: () => void;
  getPlainText: () => string;
  getRichText: () => string;
  getHtml: () => string;
  clear: () => void;
  insertText: (text: string) => void;
  setCursorLocationToStart: () => void;
  insertHtml: (html: string, overwrite?: boolean) => void;
  getEditor: () => TiptapEditor | null;
  setEditable: (editable: boolean) => void;
}

export type IframeEditorRef = MutableRefObject<IframeEditorHandle | null>;

interface UseIframeSetupReturn {
  iframeEditorRef: IframeEditorRef;
}

export default function useIframeSetup({
  iframeRef,
  theme,
  styleSheetTarget,
  customElements,
  placeholder,
  defaultValue,
  disabled,
  readOnly,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  onInit,
  spellcheck,
  initOptions,
  onImageAdded,
  imagesEnabled,
  linksEnabled,
  colorsEnabled,
  imageInsertionMode,
  pastedImages,
  editorId,
  required,
  setTiptapEditor,
  setFocused,
  autoResize = true,
  onTabOut,
  onFocusTableMenu,
  onFocusPreviousCellMenu,
  secure = false,
  preserveTableStyles = false
}: UseIframeSetupParams): UseIframeSetupReturn {
  const {
    components: {
      'text-area': { 'min-height': textAreaMinHeight }
    }
  } = theme;
  const iframeRootRef = useRef<Root | null>(null);
  const iframeEditorRef = useRef<IframeEditorHandle | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const isInitializedRef = useRef(false);
  const savedContentRef = useRef<string | null>(null);
  const [reinitializeCounter, setReinitializeCounter] = useState(0);

  // Refs to keep callbacks fresh without re-initializing the iframe
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const onImageAddedRef = useRef(onImageAdded);
  onImageAddedRef.current = onImageAdded;

  const onTabOutRef = useRef(onTabOut);
  onTabOutRef.current = onTabOut;

  const requiredRef = useRef(required);
  requiredRef.current = required;

  const onFocusTableMenuRef = useRef(onFocusTableMenu);
  onFocusTableMenuRef.current = onFocusTableMenu;

  const onFocusPreviousCellMenuRef = useRef(onFocusPreviousCellMenu);
  onFocusPreviousCellMenuRef.current = onFocusPreviousCellMenu;

  // Listen for iframe load events (fires when iframe reloads due to DOM move)
  useEffect(() => {
    if (!iframeRef.current) return;

    const iframe = iframeRef.current;

    const handleLoad = () => {
      // Check if content was lost
      const proseMirror = iframe.contentDocument?.querySelector('.ProseMirror');
      if (!proseMirror && isInitializedRef.current) {
        // savedContentRef is updated on every change, so it has the latest content
        isInitializedRef.current = false;
        setReinitializeCounter(c => c + 1);
      }
    };

    iframe.addEventListener('load', handleLoad);

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [iframeRef]);

  // Mount Tiptap inside iframe
  useLayoutEffect(() => {
    if (!iframeRef.current) return;

    // Check if already initialized and content is still valid
    if (isInitializedRef.current) {
      const proseMirror = iframeRef.current.contentDocument?.querySelector('.ProseMirror');
      if (proseMirror) {
        // Already initialized and content is valid, skip
        return;
      }
      // Content was lost (iframe reloaded), need to reinitialize
      if (iframeRootRef.current) {
        try {
          iframeRootRef.current.unmount();
        } catch (e) {
          // Ignore unmount errors
        }
        iframeRootRef.current = null;
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    }

    const iframe = iframeRef.current;
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

    if (!iframeDoc) return;

    // Generate styles
    const htmlStyles = serialize(compile(getHtmlStyles(theme)), stringify);

    // When preserving table styles, remove the default Tiptap table borders
    // so only the original inline styles show through
    const preserveTableStylesCSS = preserveTableStyles
      ? `
      .ProseMirror table,
      .ProseMirror table tr,
      .ProseMirror table td,
      .ProseMirror table th,
      .ProseMirror table tr td,
      .ProseMirror table tr th {
        border: 0 none transparent;
      }
    `
      : '';

    const contentStyle = `
      ${createGlobalRootStyles(theme)}
      ${globalSpacingStyles}
      ${createGlobalBodyStyles(theme)}
      ${htmlStyles}
      ${getIframeContentStyles(theme)}
      ${initOptions?.contentStyle ?? ''}
      ${preserveTableStylesCSS}
    `;

    // Create basic HTML structure in iframe
    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <style${window.__webpack_nonce__ ? ` nonce="${window.__webpack_nonce__}"` : ''}>
            ${contentStyle}
          </style>
        </head>
        <body>
          <div id="tiptap-root"></div>
        </body>
      </html>
    `);
    iframeDoc.close();

    // Copy global styles into the iframe
    const globalStyles =
      styleSheetTarget?.querySelectorAll('[data-cosmos-global-style]') ?? ([] as HTMLElement[]);
    const extraStyles =
      document.querySelectorAll('[data-cosmos-global-style]') ?? ([] as HTMLElement[]);
    const iframeHead = iframeDoc.querySelector('head');
    [...globalStyles, ...extraStyles].forEach(sheet => {
      iframeHead?.appendChild(sheet.cloneNode(true));
    });

    // Register custom components in iframe
    if (customElements && customElements.length > 0 && iframe.contentWindow) {
      const iframeWindow = iframe.contentWindow as unknown as typeof globalThis;
      customElements.forEach((component: CustomComponent) => {
        if (
          component.name &&
          component.createCustomElement &&
          !iframeWindow.customElements.get(component.name)
        ) {
          const CustomElement = component.createCustomElement(iframeWindow);
          iframeWindow.customElements.define(component.name, CustomElement);
        }

        // Inject custom component styles into iframe if provided
        if (component.style && iframeHead && component.name) {
          const styleId = `custom-component-${component.name}`;
          if (!iframeDoc.getElementById(styleId)) {
            const styleElement = iframeDoc.createElement('style');
            styleElement.id = styleId;
            styleElement.textContent = component.style;
            if (window.__webpack_nonce__) {
              styleElement.nonce = window.__webpack_nonce__;
            }
            iframeHead.appendChild(styleElement);
          }
        }
      });
    }

    // Mount React component inside iframe
    const rootElement = iframeDoc.getElementById('tiptap-root');
    if (rootElement && !iframeRootRef.current) {
      // Use saved content if available (from DOM move), otherwise use defaultValue
      const initialContent = savedContentRef.current ?? defaultValue;

      iframeRootRef.current = createRoot(rootElement);
      iframeRootRef.current.render(
        <IframeTiptapEditor
          placeholder={placeholder}
          defaultValue={initialContent}
          disabled={disabled}
          readOnly={readOnly}
          onChange={editor => {
            // Save content on every change so it can be restored if iframe reloads
            savedContentRef.current = editor.getHTML();
            onChangeRef.current?.(editor);
          }}
          onKeyDown={onKeyDown}
          onFocus={() => {
            setFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          onInit={(editor: TiptapEditor) => {
            // Save initial content so it can be restored if iframe reloads before any changes
            savedContentRef.current = editor.getHTML();
            // Set aria-required on the contenteditable element
            if (requiredRef.current) {
              editor.view.dom.setAttribute('aria-required', 'true');
            }
            setTiptapEditor(editor);
            onInit?.(editor);
          }}
          editorRef={iframeEditorRef}
          spellcheck={spellcheck}
          customElements={customElements}
          initOptions={initOptions}
          onImageAdded={(image, id, altText) => onImageAddedRef.current?.(image, id, altText)}
          imagesEnabled={imagesEnabled}
          linksEnabled={linksEnabled}
          colorsEnabled={colorsEnabled}
          imageInsertionMode={imageInsertionMode}
          pastedImagesRef={pastedImages}
          editorId={editorId}
          secure={secure}
          preserveTableStyles={preserveTableStyles}
          onFocusTableMenu={() => onFocusTableMenuRef.current?.()}
          onFocusPreviousCellMenu={() => {
            onFocusPreviousCellMenuRef.current?.();
          }}
        />
      );
    }

    // Handle mousedown on empty space to enable drag-to-select from anywhere
    const handleIframeMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Only handle mousedown on empty space (not on actual content like paragraphs, text nodes)
      if (
        target === iframeDoc.documentElement ||
        target === iframeDoc.body ||
        target.id === 'tiptap-root'
      ) {
        const editor = iframeEditorRef.current?.getEditor();
        if (editor) {
          // Find nearest document position and place cursor there
          // This allows ProseMirror to handle the subsequent drag selection
          const pos = editor.view.posAtCoords({ left: e.clientX, top: e.clientY });
          if (pos) {
            editor.commands.setTextSelection(pos.pos);
            editor.commands.focus();
          } else {
            editor.commands.focus('end');
          }
        }
      }
    };

    // Handle mouseup on empty space to place cursor/deselect on simple clicks
    const handleIframeMouseUp = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Only handle clicks on empty space (html, body, tiptap-root, or ProseMirror container)
      if (
        target === iframeDoc.documentElement ||
        target === iframeDoc.body ||
        target.id === 'tiptap-root' ||
        target.classList.contains('ProseMirror')
      ) {
        const editor = iframeEditorRef.current?.getEditor();
        if (editor) {
          // Check if user has a text selection (from drag) - if so, don't interfere
          const { from, to } = editor.state.selection;
          if (from === to) {
            // Selection is collapsed (no text selected), treat as click
            const pos = editor.view.posAtCoords({ left: e.clientX, top: e.clientY });
            if (pos) {
              // Place cursor at the nearest position
              editor.commands.setTextSelection(pos.pos);
              editor.commands.focus();
            } else {
              // Fallback to end of document if no position found
              editor.commands.focus('end');
            }
          }
        }
      }
    };

    // Handle Tab key - indent list items or exit editor to toolbar
    // Handle Alt+F10 - standard accessibility shortcut to focus toolbar
    const handleIframeKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F10' && e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey) {
        e.preventDefault();
        onTabOutRef.current?.();
        return;
      }
      if (e.key === 'Tab') {
        const editor = iframeEditorRef.current?.getEditor();
        if (editor) {
          const isInList = editor.isActive('listItem');
          if (isInList) {
            e.preventDefault();
            if (e.shiftKey) {
              editor.chain().focus().liftListItem('listItem').run();
            } else {
              editor.chain().focus().sinkListItem('listItem').run();
            }
            return;
          }
        }
        if (!e.shiftKey) {
          e.preventDefault();
          onTabOutRef.current?.();
        }
      }
    };

    iframeDoc.addEventListener('mousedown', handleIframeMouseDown);
    iframeDoc.addEventListener('mouseup', handleIframeMouseUp);
    iframeDoc.addEventListener('keydown', handleIframeKeyDown);

    // Auto-resize iframe based on content height (only if autoResize is enabled)
    if (autoResize) {
      const updateIframeHeight = () => {
        if (iframe.contentDocument?.body) {
          const contentHeight = iframe.contentDocument.body.scrollHeight;
          // Set min height from theme, grow with content
          // Convert rem to pixels using root font size
          const remValue = parseFloat(textAreaMinHeight) || 3;
          const rootFontSize =
            parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
          const minHeight = remValue * rootFontSize;
          iframe.style.height = `${Math.max(contentHeight + 16, minHeight)}px`;
        }
      };

      // Observe changes to iframe content
      if (iframeDoc.body && !resizeObserverRef.current) {
        resizeObserverRef.current = new ResizeObserver(updateIframeHeight);
        resizeObserverRef.current.observe(iframeDoc.body);
      }

      // Initial height update
      updateIframeHeight();
    }

    // Mark as initialized
    isInitializedRef.current = true;

    return () => {
      isInitializedRef.current = false;
      iframeDoc.removeEventListener('mousedown', handleIframeMouseDown);
      iframeDoc.removeEventListener('mouseup', handleIframeMouseUp);
      iframeDoc.removeEventListener('keydown', handleIframeKeyDown);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      // Defer unmounting to avoid race condition during React's render phase
      if (iframeRootRef.current) {
        const rootToUnmount = iframeRootRef.current;
        iframeRootRef.current = null;
        // Use setTimeout to defer unmount until after React finishes rendering
        setTimeout(() => {
          rootToUnmount.unmount();
        }, 0);
      }
    };
  }, [reinitializeCounter]);

  // Keep aria-required in sync when `required` prop changes
  useEffect(() => {
    const editorEl = iframeEditorRef.current?.getEditor()?.view.dom;
    if (!editorEl) return;

    if (required) {
      editorEl.setAttribute('aria-required', 'true');
    } else {
      editorEl.removeAttribute('aria-required');
    }
  }, [required]);

  return { iframeEditorRef };
}
